import translations from './i18n.json';
import { translateInComponent } from 'infras/i18n';

import { useState, useEffect, useContext, useMemo, useCallback } from 'react';

import { apiPath, scheduledEventListPath } from 'presentations/routes/path';
import { destroy, patch } from 'infras/rest_client';
import { Status } from 'domains/models/event/status';
import { UserScheduleCount, ViewerItem } from 'presentations/api/schema/user';
import { EventListItem } from 'presentations/api/schema/event';
import { useRequest } from 'presentations/views/hooks/request';
import { useRouter } from 'next/router';
import { EventTypeType } from 'domains/models/event_type/eventTypeType';
import { PendingEventListItem } from 'presentations/api/schema/pending_event';
import {
  DashboardPortalContext,
  setInfoMessage,
} from 'presentations/reducers/DashboardPortal';
import { setEvent } from 'presentations/views/helpers/analytics';
import { currentTimeZone, DateTime, getDateTime } from 'infras/datetime';
import {
  getPersistentItemWithViewer,
  infiniteCacheExpiration,
  setPersistentItemWithViewer,
} from 'presentations/views/helpers/storage';
import { EventTypeListItem } from 'presentations/api/schema/event_type';
import { Status as EventTypeStatus } from 'domains/models/event_type/status';
import { UserListFilter } from 'presentations/views/components/organisms/UserListFilterNavigation';
import FormExportFeature from 'domains/models/subscription/features/form_export';

interface Props {
  viewer: ViewerItem;
}

export const schedulesSortAndOrderKey = 'schedulesSortAndOrder';

export enum EventStatus {
  all = 'all',
  active = 'active',
  cancelled = 'cancelled',
}
export enum Sort {
  created = 'created',
  startDate = 'startDate',
}
export enum Order {
  asc = 'asc',
  desc = 'desc',
}

export const unsetEventTypeFilter = 'none';
const perPage = 20;

export function useScheduledEventListPage(props: Props) {
  const t = translateInComponent(translations);
  const router = useRouter();
  const timeZone = currentTimeZone();

  const { dispatch: navigationDispatch } = useContext(DashboardPortalContext);

  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [events, setEvents] = useState<EventListItem[]>([]);
  const [activeTab, setActiveTab] = useState(0);
  const [eventTypeFilter, setEventTypeFilter] = useState<string | null>(
    unsetEventTypeFilter,
  );
  const [eventStatusFilter, setEventStatusFilter] = useState<EventStatus>(
    EventStatus.all,
  );
  const [startedAtFilter, setStartedAtFilter] = useState<DateTime | null>(null);
  const [endedAtFilter, setEndedAtFilter] = useState<DateTime | null>(null);
  const defaultSort = Sort.created;
  const [sort, setSort] = useState<Sort>(defaultSort);
  const defaultOrder = Order.desc;
  const [order, setOrder] = useState<Order>(defaultOrder);

  const page = router.query.page ? Number(router.query.page) : undefined;
  const [
    deletePendingEvent,
    setDeletePendingEvent,
  ] = useState<PendingEventListItem | null>(null);
  const [isConfirmLoading, setIsConfirmLoading] = useState<boolean>(false);
  const [isShowingPlanUpgrade, setIsShowingPlanUpgrade] = useState<boolean>(
    false,
  );

  let currentUserId: string | null = null;
  if (router.query.user) {
    const userId = String(router.query.user);
    currentUserId = userId;
  }
  const projectUid = router.query.project ? String(router.query.project) : null;

  const eventsParams = {
    userId: currentUserId || undefined,
    projectId: projectUid || undefined,
    isDone: activeTab === 1 ? 'true' : 'false',
    page,
    eventTypeId:
      eventTypeFilter !== unsetEventTypeFilter ? eventTypeFilter : undefined,
    startedAt: startedAtFilter
      ? startedAtFilter.startOf('day').toJSDate().getTime()
      : undefined,
    endedAt: endedAtFilter
      ? endedAtFilter.endOf('day').toJSDate().getTime()
      : undefined,
    sort,
    order,
    status:
      eventStatusFilter === EventStatus.active
        ? Status.Open
        : eventStatusFilter === EventStatus.cancelled
        ? Status.Cancel
        : undefined,
  };
  const eventsRequest = useRequest(
    isInitialized && activeTab !== 2
      ? activeTab !== 3
        ? apiPath(`/v1/teams/${props.viewer.team!.uid}/events`, {
            ...eventsParams,
            perPage,
          })
        : apiPath(`/v1/users/${props.viewer.uid}/events`, {
            ...eventsParams,
            perPage,
          })
      : null,
  );

  const pendingEventsRequest = useRequest(
    isInitialized && activeTab === 2
      ? apiPath(`/v1/teams/${props.viewer.team!.uid}/pending_events`, {
          page,
          perPage,
          userId: currentUserId,
          eventTypeType: EventTypeType.Custom,
        })
      : null,
  );
  const pendingEvents: PendingEventListItem[] = pendingEventsRequest.data || [];

  const scheduleCountRequest = useRequest(
    projectUid
      ? apiPath(
          `/v1/teams/${
            props.viewer.team!.uid
          }/schedules_count?projectUid=${projectUid}`,
        )
      : apiPath(
          `/v1/teams/${props.viewer.team!.uid}/schedules_count?userId=${
            currentUserId || props.viewer.uid
          }`,
        ),
  );

  const schedulesCount: UserScheduleCount | null = scheduleCountRequest.data;

  const eventTypesRequest = useRequest(
    isInitialized && activeTab !== 2
      ? apiPath(`/v1/teams/${props.viewer.team!.uid}/event_types`, {
          page: 1,
          perPage: 100,
          userId: currentUserId,
          projectId: projectUid,
          sortType: 'recentUsed',
          eventTypeType: EventTypeType.Standard,
        })
      : null,
  );
  const eventTypes: EventTypeListItem[] = eventTypesRequest.data || [];
  const enabledEventTypes = useMemo(
    () => eventTypes.filter((e) => e.status === EventTypeStatus.Enable),
    [eventTypes],
  );
  const disabledEventTypes = useMemo(
    () => eventTypes.filter((e) => e.status === EventTypeStatus.Disable),
    [eventTypes],
  );

  useEffect(() => {
    const sortAndOrder = getPersistentItemWithViewer(
      schedulesSortAndOrderKey,
      props.viewer,
    );
    if (sortAndOrder && typeof sortAndOrder === 'object') {
      const newSort = sortAndOrder.sort ?? sort;
      const newOrder = sortAndOrder.order ?? order;
      if (newSort !== defaultSort || newOrder !== defaultOrder) {
        router.replace(
          scheduledEventListPath({
            userId: currentUserId,
            projectId: projectUid,
            tab:
              typeof router.query.tab === 'string'
                ? router.query.tab
                : undefined,
            filters: { ...router.query, sort: newSort, order: newOrder },
          }),
        );
      }
    }
  }, [currentUserId, projectUid, activeTab]);

  useEffect(() => {
    if (!router.isReady) return;
    if (router.query.createdByPoll !== '1') return;

    navigationDispatch(setInfoMessage(t('Created By Poll')));
  }, [router.isReady]);

  // useEffect(() => {
  //   window.scrollTo(0, 0);
  // }, [currentPage]);

  useEffect(() => {
    if (eventsRequest.data) {
      setEvents(eventsRequest.data);
    }
  }, [eventsRequest.data]);

  // router.query
  useEffect(() => {
    if (!router.isReady) return;

    if (router.query.tab) {
      setActiveTab(Number(router.query.tab));
    } else {
      setActiveTab(0);
    }

    if (typeof router.query.eventTypeId === 'string') {
      setEventTypeFilter(router.query.eventTypeId);
    } else {
      setEventTypeFilter(unsetEventTypeFilter);
    }

    if (typeof router.query.eventStatus === 'string') {
      setEventStatusFilter(router.query.eventStatus as EventStatus);
    } else {
      setEventStatusFilter(EventStatus.all);
    }

    if (
      typeof router.query.startedAt === 'string' &&
      typeof router.query.endedAt === 'string'
    ) {
      setStartedAtFilter(getDateTime(Number(router.query.startedAt), timeZone));
      setEndedAtFilter(getDateTime(Number(router.query.endedAt), timeZone));
    } else {
      setStartedAtFilter(null);
      setEndedAtFilter(null);
    }

    if (typeof router.query.sort === 'string') {
      setSort(router.query.sort as Sort);
    } else {
      setSort(Sort.created);
    }

    if (typeof router.query.order === 'string') {
      setOrder(router.query.order as Order);
    } else {
      setOrder(Order.desc);
    }

    setIsInitialized(true);
  }, [
    router.isReady,
    router.query.tab,
    router.query.eventTypeId,
    router.query.eventStatus,
    router.query.startedAt,
    router.query.endedAt,
    router.query.sort,
    router.query.order,
  ]);

  const onChangeFilter = (filters: UserListFilter) => {
    const { userId, projectId } = filters;
    const tab =
      typeof router.query.tab === 'string' ? router.query.tab : undefined;

    router.push(
      scheduledEventListPath({
        userId: userId === props.viewer.uid ? null : userId,
        projectId,
        tab,
        filters: { ...router.query, eventTypeId: undefined },
      }),
    );
  };

  const onChangeEventTypeFilter = (e: any) => {
    const newValue = e.currentTarget.value;
    router.replace(
      scheduledEventListPath({
        userId: currentUserId,
        projectId: projectUid,
        tab:
          typeof router.query.tab === 'string' ? router.query.tab : undefined,
        filters: {
          ...router.query,
          eventTypeId:
            newValue === unsetEventTypeFilter ? undefined : String(newValue),
        },
      }),
    );
  };

  const onChangeEventStatusFilter = (e: any) => {
    const newValue = e.currentTarget.value;
    router.replace(
      scheduledEventListPath({
        userId: currentUserId,
        projectId: projectUid,
        tab:
          typeof router.query.tab === 'string' ? router.query.tab : undefined,
        filters: {
          ...router.query,
          eventStatus: newValue,
        },
      }),
    );
  };

  const onChangeDateFilter = useCallback(
    (startedAt?: Date, endedAt?: Date) => {
      router.replace(
        scheduledEventListPath({
          userId: currentUserId,
          projectId: projectUid,
          tab:
            typeof router.query.tab === 'string' ? router.query.tab : undefined,
          filters: {
            ...router.query,
            startedAt: startedAt ? String(startedAt.getTime()) : undefined,
            endedAt: endedAt ? String(endedAt.getTime()) : undefined,
          },
        }),
      );
    },
    [
      currentUserId,
      router.query.tab,
      router.query.eventTypeId,
      router.query.eventStatus,
      router.query.startedAt,
      router.query.endedAt,
      router.query.sort,
      router.query.order,
      projectUid,
    ],
  );

  const onChangeSort = (e: any) => {
    const newValue = e.currentTarget.value;
    setPersistentItemWithViewer(
      schedulesSortAndOrderKey,
      { sort: newValue, order },
      infiniteCacheExpiration,
      props.viewer,
    );
    router.replace(
      scheduledEventListPath({
        userId: currentUserId,
        projectId: projectUid,
        tab:
          typeof router.query.tab === 'string' ? router.query.tab : undefined,
        filters: { ...router.query, sort: newValue },
      }),
    );
  };

  const onChangeOrder = (e: any) => {
    const newValue = order === 'asc' ? 'desc' : 'asc';
    setPersistentItemWithViewer(
      schedulesSortAndOrderKey,
      { sort, order: newValue },
      infiniteCacheExpiration,
      props.viewer,
    );
    router.replace(
      scheduledEventListPath({
        userId: currentUserId,
        projectId: projectUid,
        tab:
          typeof router.query.tab === 'string' ? router.query.tab : undefined,
        filters: { ...router.query, order: newValue },
      }),
    );
  };

  const onClickCsvExport = (e: any) => {
    const formExportFeature = new FormExportFeature({
      planVersion: props.viewer.planVersion,
    });
    if (!formExportFeature.isValid(props.viewer.planId)) {
      e.preventDefault();
      setIsShowingPlanUpgrade(true);
    }
  };

  const onChangePage = (e: React.ChangeEvent<unknown>, value: number) => {
    router.replace(
      scheduledEventListPath({
        userId: currentUserId,
        projectId: projectUid,
        tab:
          typeof router.query.tab === 'string' ? router.query.tab : undefined,
        filters: { ...router.query },
        page: value,
      }),
    );
  };

  const onCancelEvent = async (event: EventListItem) => {
    if (!event) return;
    const result = await patch(
      apiPath(`/v1/teams/${props.viewer.team!.uid}/events/${event.id}/cancel`),
    );
    if (!result.isSuccess) {
      return;
    }
    const newEvents = events.map((e) => {
      if (e.uid === event.uid && 'status' in e) {
        e.status = Status.Cancel;
      }
      return e;
    });
    setEvents(newEvents);
  };

  const onDeleteEvent = async () => {
    if (!deletePendingEvent) return;
    setIsConfirmLoading(true);
    const result = await destroy(
      apiPath(
        `/v1/teams/${props.viewer.team!.uid}/pending_events/${
          deletePendingEvent.uid
        }/delete`,
      ),
    );
    if (!result.isSuccess) {
      setDeletePendingEvent(null);
      setIsConfirmLoading(false);
      return;
    }

    setEvent('pending event delete', {
      pendingEventType: deletePendingEvent.pendingEventType,
    });

    const newPendingEvents = pendingEvents.filter(
      (e) => e.id !== deletePendingEvent.id,
    );
    pendingEventsRequest.mutate(
      { ...pendingEventsRequest, data: newPendingEvents },
      false,
    );
    setDeletePendingEvent(null);
    setIsConfirmLoading(false);
  };

  const onSelectMenu = async (
    pendingEvent: PendingEventListItem,
    selectedIndex: number,
  ) => {
    switch (selectedIndex) {
      case 0: {
        setDeletePendingEvent(pendingEvent);
        break;
      }
      default:
        break;
    }
  };

  const onCloseDialog = () => {
    setDeletePendingEvent(null);
  };

  const onClosePlanUpgrade = () => {
    setIsShowingPlanUpgrade(false);
  };

  const formExportFeature = new FormExportFeature({
    planVersion: props.viewer.planVersion,
  });

  return {
    state: {
      events,
      activeTab,
      eventTypeFilter,
      startedAtFilter,
      endedAtFilter,
      sort,
      order,
      isLoading: eventsRequest.isLoading,
      currentPage: page || 1,
      numberOfPages:
        activeTab === 2
          ? pendingEventsRequest.numberOfPages
          : eventsRequest.numberOfPages,
      currentUserId,
      isLoadingEventType: pendingEventsRequest.isLoading,
      pendingEvents,
      deletePendingEvent,
      isConfirmLoading,
      locale: router.locale,
      query: router.query,
      timeZone,
      projectUid,
      enabledEventTypes,
      disabledEventTypes,
      isShowingPlanUpgrade,
      csvExportUrl: formExportFeature.isValid(props.viewer.planId)
        ? apiPath(
            `/v1/teams/${props.viewer.team!.uid}/events/export_csv`,
            eventsParams,
          )
        : undefined,
      eventStatusFilter,
      isInitialized,
      pendingCount: schedulesCount?.pending || 0,
      incomingCount: schedulesCount?.incoming || 0,
    },
    actions: {
      onChangePage,
      onCancelEvent,
      onChangeFilter,
      onChangeEventTypeFilter,
      onChangeDateFilter,
      onChangeSort,
      onChangeOrder,
      onClickCsvExport,
      onSelectMenu,
      onDeleteEvent,
      onCloseDialog,
      onClosePlanUpgrade,
      onChangeEventStatusFilter,
    },
  };
}
